import { z } from 'zod';
import { Validation, createZodFormResolver } from '@mews/framework';
import { FILE_SCHEMA_OPTIONS } from '../../common/constants';
import { GenerateContractFormStepName, RecipientsFieldName } from './types';
import { MAX_CHARACTER_COUNT_MESSAGE, MAX_CHARACTER_COUNT_SUBJECT } from './constants';

export const uploadFileSchema = (existingNames: string[]) =>
    z.object({
        file: Validation.File.Default(FILE_SCHEMA_OPTIONS).refine((file) => file.length > 0, {
            message: 'ContractsFileRequiredValidationMessage',
        }),
        name: z
            .string()
            .min(1)
            .max(255)
            .refine((name) => existingNames == null || !existingNames.includes(name), {
                message: 'ContractFormDuplicateNameValidationMessage',
            }),
    });

const expirationDateSchema = z
    .object({
        expirationDate: Validation.Date.Iso.nullish(),
        expirationDateEnabled: z.boolean().default(false),
    })
    .refine(
        (data) => {
            return !(data.expirationDateEnabled && !data.expirationDate);
        },
        {
            message: 'ContractsGenerateExpirationDateRequiredValidationMessage',
            path: ['expirationDate'],
        }
    );
const selectedAttachmentSchema = z.object({ id: z.string().uuid() }).and(expirationDateSchema);

const uploadedFilesSchema = (existingNames: string[]) =>
    z.array(uploadFileSchema(existingNames).and(expirationDateSchema)).optional();

const templateStepSchema = (existingNames: string[]) =>
    z
        .object({
            templates: z.object({
                uploadedFiles: uploadedFilesSchema(existingNames),
                selectedAttachments: z.array(selectedAttachmentSchema).optional(),
                selectedTemplates: z.record(expirationDateSchema),
            }),
        })
        .refine((data) => Object.keys(data.templates.selectedTemplates).length > 0, {
            message: 'ContractsGenerateTemplateRequiredValidationMessage',
            path: ['templates', 'selectedTemplates', 'root'],
        });

export type TemplateStepValues = z.infer<ReturnType<typeof templateStepSchema>>;

export const emailSchema = z.object({
    subject: Validation.String.Required.max(MAX_CHARACTER_COUNT_SUBJECT),
    message: Validation.String.Required.max(MAX_CHARACTER_COUNT_MESSAGE),
});

const recipientsStepSchema = z.object({
    recipients: z.object({
        recipients: z.record(
            z.object({
                [RecipientsFieldName.Email]: Validation.String.Required.email(),
                [RecipientsFieldName.Name]: Validation.String.Required.max(255),
            })
        ),
        email: emailSchema,
        signatureDeadlineDate: Validation.Date.Iso.optional(),
    }),
});
export type RecipientsStepValues = z.infer<typeof recipientsStepSchema>;

export const generateContractFormSchema = (existingNames: string[]) => ({
    [GenerateContractFormStepName.Templates]: createZodFormResolver(templateStepSchema(existingNames)),
    [GenerateContractFormStepName.Recipients]: createZodFormResolver(recipientsStepSchema),
});

export const generateContractFormSteps = [
    GenerateContractFormStepName.Templates,
    GenerateContractFormStepName.Recipients,
] as const;
